class ProductInteractiveOptions extends HTMLElement {
  constructor() {
    super();
    this.variantsControlElement = this.parentElement;
    this.variants = this.variantsControlElement.getVariantData();
    this.inputs = this.getOptionsElementsObject();
    this.options = Object.keys(this.inputs).reduce((options, key) => {
      // eslint-disable-next-line no-param-reassign
      options[this.inputs[key].position] = key;
      return options;
    }, []);

    /**
     * Combined listings (2026-08-31 fix): on CHILD product pages Shopify
     * injects the PARENT'S "Color" option into the picker, but the local
     * variant JSON only carries the child's own options (e.g. just Size).
     * All the logic below pairs rendered option groups with variant.options
     * BY INDEX, so the injected leading Color group shifted everything by
     * one: no variant ever matched the current selection, currentVariant
     * came back null, and the constructor guard skipped
     * setOptionInputsAvailability() entirely — sold-out sizes lost their
     * strikethrough after every colour switch. Fix: re-align this.options
     * so index i names the rendered group whose VALUES actually occur at
     * variant.options[i]; injected groups align to no index and are left
     * alone (their sibling values are navigation links anyway).
     */
    this.options = this.getVariantAlignedOptionNames();
    this.managedOptionNames = this.options.filter((name) => name !== null && name !== undefined);

    this.currentVariant = this.getCurrentVariant();

    // Combined listings: on pages where the picker includes option values
    // that belong to sibling child products, the full selection may not
    // match any local variant. Guard instead of crashing — sibling values
    // navigate to their own product page (see setUpInputListeners).
    if (this.currentVariant) this.setOptionInputsAvailability();
    this.setUpInputListeners();

    if (this.dataset.variantId) {
      const id = Number(this.dataset.variantId);
      this.selectVariantById(id);
    }
  }

  getOptionsElementsObject() {
    const options = Array.from(this.variantsControlElement.querySelectorAll('input, select > option'));
    const obj = options.reduce((finalObject, el) => {
      if (el.nodeName === 'INPUT') {
        const optionName = el.name.trim();
        // eslint-disable-next-line no-param-reassign
        if (!finalObject.hasOwnProperty(optionName)) finalObject[optionName] = {
          position: Object.keys(finalObject).length,
          type: 'inputs',
          elements: {},
        }
        // eslint-disable-next-line no-param-reassign
        finalObject[optionName].elements[el.value] = {
          element: el
        };
      } else if (el.nodeName === 'OPTION') {
        const selectElement = el.parentElement;
        const optionName = selectElement.name.replace('options[', '').replace(']', '').trim();
        if (!finalObject.hasOwnProperty(optionName)) {
          // eslint-disable-next-line no-param-reassign
          finalObject[optionName] = {
            position: Object.keys(finalObject).length,
            type: 'select',
            elements: {},
            selectElement,
          }
        }
        // eslint-disable-next-line no-param-reassign
        finalObject[optionName].elements[el.value] = {
          element: el,
          text: el.innerHTML.trim(),
        };
      }
      return finalObject;
    }, {});

    return obj;
  }

  /**
   * Map each LOCAL variant option position to the rendered option group
   * whose values occur at that position. Rendered groups that match no
   * position (combined-listing injected options) are not managed here.
   *
   * @returns {(string | null)[]} Group name per variant option index
   */
  getVariantAlignedOptionNames() {
    const renderedNames = Object.keys(this.inputs);
    if (!this.variants.length) return this.options;

    const optionCount = this.variants[0].options.length;
    const aligned = [];

    for (let j = 0; j < optionCount; j += 1) {
      const valuesAtJ = new Set(this.variants.map((v) => String(v.options[j]).trim()));
      const match = renderedNames.find(
        (name) =>
          aligned.indexOf(name) === -1 &&
          Object.keys(this.inputs[name].elements).some((v) => valuesAtJ.has(String(v).trim())),
      );
      aligned.push(match !== undefined ? match : null);
    }

    return aligned;
  }

  getCurrentOptions() {
    // Read selection in VARIANT option order (see getVariantAlignedOptionNames);
    // null for positions whose group isn't rendered (matches any value).
    return this.options.map((name) => {
      const group = name !== null && name !== undefined ? this.inputs[name] : null;
      if (!group) return null;
      if (group.type === 'select') return group.selectElement.value;
      const checked = Object.keys(group.elements).find((v) => group.elements[v].element.checked);
      return checked !== undefined ? checked : null;
    });
  }

  getCurrentVariant() {
    const options = this.getCurrentOptions();

    const result = this.variants.filter((variant) =>
      options.every((option, index) =>
        option === null || variant.options[index] === option
      )
    );

    return result[0] || null;
  }

  /**
   * Get variant object by variant id
   *
   * @param {number} id Variant id
   * @returns {(Object | null)} Variant or null if none found
   */
  getVariantById(id) {
    const [result] = this.variants.filter((variant) => variant.id === id);

    return result || null;
  }

  /**
   * Based on the current variant, return all options present
   * in available variants that start with the same main option
   * as the current variant, and include the same options in their
   * option list.
   *
   * @returns {string[]} Array of options
   */
  optionsAvailableInOtherVariants() {
    const currentVariant = this.getCurrentVariant();

    const availableVariants = this.variants.filter(
      (variant) =>
        variant.available &&
        variant.id !== currentVariant.id &&
        variant.options[0] === currentVariant.options[0],
    );

    return currentVariant.options.reduce((options, option, i) => {
      if (
        availableVariants.some((variant) =>
          variant.options
            .map((o, j) => `${o}_${j}`)
            .includes(`${option}_${i}`),
        )
      )
        options.push(`${option}_${i}`);

      return options;
    }, []);
  };

  /**
   * Setup product options display (available, unavailable, disabled)
   */
  setOptionInputsAvailability() {
    // Need to cache current variant for correct functionality
    // of option resets
    this.currentVariant = this.getCurrentVariant();

    // Combined listings guard: no local variant matches (selection includes
    // sibling-product option values). Leave inputs interactive — sibling
    // values navigate to their own product page.
    if (!this.currentVariant) return;

    // Disable all options (managed groups only — combined-listing injected
    // groups keep their own state; their values are navigation links)
    this.managedOptionNames.forEach((option) => {
      Object.keys(this.inputs[option].elements).forEach((value) => {
        const { element, text } = this.inputs[option].elements[value];

        // Combined listings: never disable a sibling-product option value.
        if (element.dataset && element.dataset.productUrl) return;

        element.disabled = true;

        if (this.inputs[option].type === 'inputs') element.nextElementSibling.classList.add('is-disabled');
        if (this.inputs[option].type === 'select') element.innerHTML = `${text} - Unavailable`;
      });
    });

    // Get all the options from neighbouring available variants
    // const availableOptions = this.optionsAvailableInOtherVariants();

    const availableOptions = new Set();

    /**
     * Combined listings (2026-08-28 fix): on a CHILD product page Shopify
     * reports ONE storefront option (the parent's "Color") but every variant
     * still carries TWO option values — ["Camo/Cream", "Default Title"] — the
     * child's own leftover Title option. So variant.options is longer than
     * this.options, and `this.inputs[this.options[1]]` was undefined:
     *
     *   TypeError: Cannot read properties of undefined (reading 'elements')
     *
     * That threw inside the CONSTRUCTOR, so setUpInputListeners() never ran.
     * Without it, maybeNavigateToSibling() was never wired up, the theme's own
     * change handler was never removed, and clicking a sibling colour selected
     * a value with no local variant instead of navigating to that colour's
     * product — leaving the PDP on "Unavailable" with the wrong colour in the
     * legend. Parent pages were unaffected (their variants have exactly one
     * option), which is why the bug only ever showed on child pages.
     *
     * Fix: never index this.inputs with an option group that wasn't rendered,
     * and never assume a rendered group contains the value we're looking up.
     */
    const inputGroupFor = (index) => {
      const name = this.options[index];
      if (name === undefined) return null;
      return this.inputs[name] || null;
    };

    // Remove disabled status from existing options;
    // remove unavailable status from available options
    this.currentVariant.options.forEach((option, i) => {
      const group = inputGroupFor(i);
      if (!group || !group.elements[option]) return;

      const optionInput = group.elements[option];
      const inputType = group.type;

      optionInput.element.disabled = false;

      // Remove 'is-disabled' class from the label if the current
      // variant is available, or the option exists in neighbouring
      // variants
      if (this.currentVariant.available) {
        availableOptions.add(`${this.options[i]}_${option}`);
        if (inputType === 'inputs') optionInput.element.nextElementSibling.classList.remove('is-disabled');
        if (inputType === 'select') optionInput.element.innerHTML = optionInput.text;
      }

      if (inputType === 'select' && !availableOptions.has(`${this.options[i]}_${option}`))
        optionInput.element.innerHTML = `${optionInput.text} - ${window.theme.t.sold_out}`;
    });

    // Cycle through all variants, and set options to unavailable or
    // available if exist in variant
    this.variants.forEach((variant) => {

      if (
        variant.id !== this.currentVariant.id &&
        variant.options[0] === this.currentVariant.options[0]
      ) {
        variant.options.every((option, i) => {
          if (
            i > 0 &&
            variant.options[i - 1] === this.currentVariant.options[i - 1]
          ) {
            const group = inputGroupFor(i);
            if (!group || !group.elements[option]) return true;

            const optionInput = group.elements[option];
            const inputType = group.type;

            optionInput.element.disabled = false;

            if (!this.currentVariant.available && this.currentVariant.options.includes(option)) return true;

            if (variant.available) {
              availableOptions.add(`${this.options[i]}_${option}`);
              if (inputType === 'inputs') optionInput.element.nextElementSibling.classList.remove('is-disabled');
              if (inputType === 'select') optionInput.element.innerHTML = optionInput.text;
            }

            if (inputType === 'select' && !availableOptions.has(`${this.options[i]}_${option}`))
              optionInput.element.innerHTML = `${optionInput.text} - ${window.theme.t.sold_out}`;
          }

          return true;
        });
      } else if (variant.id !== this.currentVariant.id) {
        // Only need the main option of the variants which don't
        // share main option with the current variant
        const group = inputGroupFor(0);
        if (!group || !group.elements[variant.options[0]]) return;

        const optionInput = group.elements[variant.options[0]];
        const inputType = group.type;

        optionInput.element.disabled = false;

        if (variant.available) {
          availableOptions.add(`${this.options[0]}_${variant.options[0]}`);
          if (inputType === 'inputs') optionInput.element.nextElementSibling.classList.remove('is-disabled');
          if (inputType === 'select') optionInput.element.innerHTML = optionInput.text;
        }

        if (inputType === 'select' && !availableOptions.has(`${this.options[0]}_${variant.options[0]}`))
          optionInput.element.innerHTML = `${optionInput.text} - ${window.theme.t.sold_out}`;
      }
    });
  };

  /**
   * Get the first available existing variant the option belongs to. If
   * no available variants exist, return the first existing one. Otherwise
   * return null.
   *
   * @param {string} option Reference option
   * @param {string} optionGroup Option group to which the option belongs
   * @returns {(Object | null)} Variant or null if none found
   */
  getFirstExistingVariant(option, optionGroup) {
    const optionPosition = this.options.indexOf(optionGroup);

    const existingVariantsWithOption = this.variants.filter(
      (variant) =>
        variant.options[optionPosition] === option &&
        (optionPosition === 0 ||
          variant.options
            .slice(0, optionPosition)
            .every((o, index) => o === this.currentVariant.options[index])),
    );

    for (let i = 0, l = existingVariantsWithOption.length; i < l; i += 1) {
      if (existingVariantsWithOption[i].available) return existingVariantsWithOption[i];
    }

    return existingVariantsWithOption.length > 0
      ? existingVariantsWithOption[0]
      : null;
  };

  /**
   * Get existing variant (either available or sold out) from
   * the option specified. Otherwise return null.
   *
   * @param {string} option Reference option
   * @param {string} optionGroup Option group to which the option belongs
   * @returns {(Object | null)} Variant or null if none found
   */
  getExistingVariantFromSelectedOption(option, optionGroup) {
    const options = this.getCurrentOptions();
    const optionPosition = this.options.indexOf(optionGroup);

    options[optionPosition] = option;

    const variant = this.variants.find((v) =>
      v.options.every((val, idx) => options[idx] === null || options[idx] === undefined || val === options[idx]),
    );
    return variant || null;
  }

  /**
   * Set product form options to reflect the selected variant.
   *
   * @param {Object} variant Product variant
   */
  selectVariant(variant) {
    const currentFocusedElement = document.activeElement;
    const optionsByGroup = variant.options.reduce(
      (grouped, option, index) => {
        // eslint-disable-next-line no-param-reassign
        grouped[this.options[index]] = option;

        return grouped;
      },
      {},
    );

    this.managedOptionNames.forEach((option) => {
      Object.keys(this.inputs[option].elements).forEach((value) => {
        const { element, text } = this.inputs[option].elements[value];

        element.disabled = false;

        if (this.inputs[option].type === 'inputs') {
          element.nextElementSibling.classList.remove('is-disabled');
          element.checked = false;
        }
        if (this.inputs[option].type === 'select') element.innerHTML = text;

        if (
          optionsByGroup[
            option
          ] === value
        ) {
          if (this.inputs[option].type === 'inputs') element.checked = true;
          if (this.inputs[option].type === 'select') this.inputs[option].selectElement.value = value;
        }
      });
    });

    this.setOptionInputsAvailability();
    if (currentFocusedElement) {
      currentFocusedElement.focus();
    }
  };

  selectVariantById(id) {
    const variant = this.getVariantById(id);

    if (variant) {
      this.selectVariant(variant);
      this.variantsControlElement.onVariantChange();
    }
  }

  /**
   * Select variant if exists, otherwise select first existing one.
   *
   * @param {string} option Reference option
   * @param {string} optionGroup Option group to which the option belongs
   */
  selectCurrentOrFirstExistingVariant(option, optionGroup) {
    const selectedVariant = this.getExistingVariantFromSelectedOption(
      option.trim(),
      optionGroup.trim(),
    );

    if (selectedVariant) {
      this.selectVariant(selectedVariant);
    } else {
      const firstExistingVariant = this.getFirstExistingVariant(
        option.trim(),
        optionGroup.trim(),
      );

      if (firstExistingVariant) {
        this.selectVariant(firstExistingVariant);
      }
    }
  }

  /**
   * Combined listings: if the chosen option value belongs to a sibling child
   * product (it carries data-product-url), navigate to that product's page
   * instead of running local variant selection. Returns true if navigating.
   *
   * @param {(HTMLElement | null)} el Element that may carry data-product-url
   * @returns {boolean} True when navigation was triggered
   */
  maybeNavigateToSibling(el) {
    if (!el || !el.dataset || !el.dataset.productUrl) return false;

    let siblingUrl = el.dataset.productUrl;
    const siblingPath = siblingUrl.split('?')[0];

    // Wash tiles (2026-08-31): carry the chosen Size across wash navigation
    // so the shopper doesn't reselect it on every colourway page. The
    // wash-tiles snippet re-applies it from ?size= on load.
    const sizeInput = this.variantsControlElement.querySelector(
      'input[name="Size"]:checked, input[name="size"]:checked',
    );
    if (sizeInput && sizeInput.value) {
      siblingUrl += (siblingUrl.indexOf('?') === -1 ? '?' : '&') + 'size=' + encodeURIComponent(sizeInput.value);
    }

    if (siblingPath && siblingPath !== window.location.pathname) {
      window.location.assign(siblingUrl);
      return true;
    }

    return false;
  }

  setUpInputListeners() {
    // Remove the onChange event from the variant-selects or variants-radios
    this.variantsControlElement.removeEventListener('change', this.variantsControlElement.onVariantChange);

    Object.keys(this.inputs).forEach((optionGroup) => {

      if (this.inputs[optionGroup].type === 'inputs') {
        Object.keys(this.inputs[optionGroup].elements).forEach((option) => {
          const input = this.inputs[optionGroup].elements[option].element;
          input.addEventListener('click', (e) => {
            if (this.maybeNavigateToSibling(e.target)) return;
            this.selectCurrentOrFirstExistingVariant(
              e.target.value.trim(),
              e.target.name.trim(),
            );
          });
        })
      } else if (this.inputs[optionGroup].type === 'select') {
        this.inputs[optionGroup].selectElement.addEventListener('change', (e) => {
          const chosen = e.target.selectedOptions && e.target.selectedOptions[0];
          if (this.maybeNavigateToSibling(chosen)) return;
          this.selectCurrentOrFirstExistingVariant(
            e.target.value.trim(),
            e.target.name.trim().replace('options[', '').replace(']', ''),
          );
        })
      }

    });

    this.variantsControlElement.addEventListener('change', this.variantsControlElement.onVariantChange);
  };
}

customElements.define('product-interactive-options', ProductInteractiveOptions);